import React from 'react';
import { SelectInput } from 'react-admin';
import { useFormState } from 'react-final-form';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';

import SanitizedTextInput from '../controls/SanitizedTextInput';
import SoftGoodLotInput from '../controls/SoftGoodLotInput';
import SOFT_GOODS, { totalQty } from '../controls/softGoods';
import { useSoftGoods, linesForField } from '../controls/softGoodsStore';

/**
 * The soft goods block of the Phlebotomy tab: one lot picker and one quantity
 * per supply, kept in step with each other.
 *
 * Both the supervisor tab and the phlebotomist's own screen render this, so
 * the two can't drift apart.
 */

const cKitChoices = ['0', '1', '2', '3', '4', '5'].map(v => ({ id: v, name: v }));

const mismatchStyle = { fontSize: 11, color: '#b26a00', display: 'block', marginTop: 2 };

/** Header + picker for the five lot numbers. */
export const SoftGoodsLotRows = ({ disabled }) => (
    <React.Fragment>
        <TableRow>
            {SOFT_GOODS.map(item => (
                <TableCell key={item.field}>Lot #: {item.label}</TableCell>
            ))}
        </TableRow>
        <TableRow>
            {SOFT_GOODS.map(item => (
                <TableCell key={item.field}>
                    <SoftGoodLotInput field={item.field} disabled={disabled} />
                </TableCell>
            ))}
        </TableRow>
    </React.Fragment>
);

/**
 * Quantity used. Filled in by the picker, but still editable - inventory is
 * not always the system of record on day one, and a phlebotomist must never
 * be blocked from finishing the worksheet. When the typed number disagrees
 * with the lots picked, say so rather than silently overwriting either.
 */
const QtyCell = ({ item, validate, disabled, ckitDefault }) => {
    const { values } = useFormState({ subscription: { values: true } });
    const softGoods = useSoftGoods();

    const lines = linesForField(softGoods, item.field);
    const picked = totalQty(lines);
    const typed = Number(values[item.qtySource]);
    const mismatch = lines.length > 0 && !isNaN(typed) && typed !== picked;

    return (
        <TableCell>
            {item.field === 'CKIT' ? (
                <SelectInput
                    source={item.qtySource}
                    label=""
                    choices={cKitChoices}
                    validate={validate}
                    disabled={disabled}
                    defaultValue={ckitDefault}
                />
            ) : (
                <SanitizedTextInput
                    source={item.qtySource}
                    label=""
                    validate={validate}
                    disabled={disabled}
                />
            )}
            {mismatch && (
                <span style={mismatchStyle}>
                    {picked} unit{picked === 1 ? '' : 's'} selected across {lines.length} lot
                    {lines.length === 1 ? '' : 's'}
                </span>
            )}
        </TableCell>
    );
};

/**
 * `ckitDefault` keeps the supervisor tab's old behaviour of pre-filling the
 * kit count with 0. The phlebotomist's own screen deliberately leaves it
 * blank, so `required()` there means a real answer rather than a default that
 * happens to validate.
 */
export const SoftGoodsQtyRows = ({ validate, disabled, ckitDefault }) => (
    <React.Fragment>
        <TableRow style={{display: "none"}}>
            {SOFT_GOODS.map(item => (
                <TableCell key={item.field}>Quantity used: {item.label}</TableCell>
            ))}
        </TableRow>
        <TableRow style={{display: "none"}}>
            {SOFT_GOODS.map(item => (
                <QtyCell
                    key={item.field}
                    item={item}
                    validate={validate}
                    disabled={disabled}
                    ckitDefault={ckitDefault}
                />
            ))}
        </TableRow>
    </React.Fragment>
);

export default SoftGoodsLotRows;
